/**
 * Submits the verified Ring Group membership Add/Remove payloads produced by
 * useCSVRingGroupMembership. Modelled on useCSVCreateBulkUpdate.js — calls
 * createRingGroupMembership for 'Add' rows and deleteRingGroupMembership for
 * 'Remove' rows, sequentially, tracking per-row success/failure for the
 * APILoadingView / APIResultsView scaffolding.
 */
import React from 'react';
import Papa from 'papaparse';
import { downloadCSV } from '../../../../utils/utils';
import {
    createRingGroupMembership,
    deleteRingGroupMembership,
} from 'pages/services/eightByEight/userManagement/api';

const DEFAULT_BULK_STATUS = { apiErrors: '' };
const ERROR_MESSAGE = 'Something went wrong. Please try again later.';
const FILENAME = 'FailedRingGroupMembershipUpdates.csv';

const FIELDS_TO_EXPORT = {
    ringGroupName: 'Ring Group Name',
    memberUPN: 'Member UPN',
    action: 'Action',
    apiErrors: 'Errors',
};

export default function useCSVRingGroupMembershipBulkUpdate() {
    const [error, setError] = React.useState([]);
    const [loading, setLoading] = React.useState(false);
    const [succeed, setSucceed] = React.useState([]);
    const [counter, setCounter] = React.useState(0);
    const [errorCounter, setErrorCounter] = React.useState(0);
    const [uploadPayloads, setUploadPayloads] = React.useState([]);

    const transformSubmitData = (payloads) =>
        payloads.map((p) => ({ ...DEFAULT_BULK_STATUS, ...p }));

    const processSubmission = async (payloads) => {
        setLoading(true);
        handleBulkUpdate(transformSubmitData(payloads));
    };

    const handleReset = () => {
        setCounter(0);
        setErrorCounter(0);
        setSucceed([]);
        setError([]);
    };

    const handleFinish = ({ errors, responses }) => {
        setError(errors);
        setSucceed(responses);
    };

    const handleBulkUpdate = async (payloads) => {
        const errors = [];
        const responses = [];
        setUploadPayloads(payloads);

        for (let index = 0; index < payloads.length; index++) {
            const payload = payloads[index];

            try {
                if (payload.type === 'addMember') {
                    await createRingGroupMembership(
                        payload.payload,
                        payload.requiredScope,
                    );
                } else if (payload.type === 'removeMember') {
                    await deleteRingGroupMembership(
                        payload.payload,
                        payload.requiredScope,
                    );
                }

                responses.push({
                    data: {
                        ...payload.meta,
                        status:
                            payload.type === 'addMember' ? 'Added' : 'Removed',
                    },
                });
            } catch (e) {
                errors.push({
                    ...payload.meta,
                    apiErrors:
                        `${e?.message}\n${e?.response?.data?.message || ''}\n${e?.response?.status || ''}` ||
                        ERROR_MESSAGE,
                });
                setErrorCounter((prev) => prev + 1);
            } finally {
                setCounter((prev) => prev + 1);
                if (index === payloads.length - 1) {
                    handleFinish({ errors, responses });
                }
            }
        }
    };

    const handlePayloadChange = React.useCallback((payloads) => {
        processSubmission(payloads);
    }, []);

    const handleExportFailedData = () => {
        const rows = error.map((item) =>
            Object.keys(FIELDS_TO_EXPORT).reduce((acc, key) => {
                acc[FIELDS_TO_EXPORT[key]] = item[key] ?? '';
                return acc;
            }, {}),
        );
        const res = Papa.unparse(rows);
        downloadCSV(res, FILENAME);
    };

    return {
        handlePayloadChange,
        loading,
        counter,
        errorCounter,
        error,
        succeed,
        handleReset,
        handleExportFailedData,
        total: uploadPayloads.length,
    };
}
